/**
 * Application navigation model.
 *
 * Ported from the `menuData` global the raw HTML design layouts declare
 * (RawHTMLDesign/layouts/*.html). The design's preview runtime navigated by
 * posting `WORKFLOW_MENU_NAVIGATION` messages carrying PageId/ModuleId to the
 * parent frame; in the real app we navigate with react-router paths instead,
 * so each item keeps its design ids for traceability and adds a `path`.
 */

export const MENU_ITEMS = [
  {
    label: 'Capture',
    path: '/capture',
    icon: 'fa fa-map-marked-alt',
    pageId: 73071,
    moduleId: 22380,
  },
  // Network sits right after Capture (Arup's ordering) — relationship context
  // is reached before working the inbox.
  {
    label: 'Network',
    path: '/network',
    icon: 'fa fa-network-wired',
    pageId: 73078,
    moduleId: 22384,
  },
  // Reach is the portfolio view of the same graph Network explores one target
  // at a time, so it sits directly beside it.
  {
    label: 'Reach',
    path: '/reach',
    icon: 'fa fa-bullseye',
    pageId: 73082,
    moduleId: 22384,
  },
  { label: 'Chats', path: '/chats', icon: 'fab fa-whatsapp', pageId: 73072, moduleId: 22380 },
  { label: 'Listings', path: '/listings', icon: 'fa fa-inbox', pageId: 73073, moduleId: 22380 },
  { label: 'Registry', path: '/registry', icon: 'fa fa-stamp', pageId: 73074, moduleId: 22381 },
  { label: 'Matching', path: '/matching', icon: 'fa fa-sliders-h', pageId: 73075, moduleId: 22382 },
  { label: 'Packs', path: '/packs', icon: 'fa fa-folder-plus', pageId: 73076, moduleId: 22382 },
  { label: 'Mandates', path: '/mandates', icon: 'fa fa-users-cog', pageId: 73077, moduleId: 22383 },
  {
    label: 'Enrich',
    path: '/enrich',
    icon: 'fa fa-map-marker-alt',
    pageId: 73079,
    moduleId: 22385,
  },
  { label: 'Refresh', path: '/refresh', icon: 'fa fa-sync-alt', pageId: 73080, moduleId: 22386 },
  { label: 'Deals', path: '/deals', icon: 'fa fa-handshake', pageId: 73081, moduleId: 22387 },
];

/** The design's `DefaultMenuItem` — where "/" should land. */
export const DEFAULT_MENU_PATH = '/listings';

/**
 * The design's mobile bottom bar shows a flattened, capped slice of the menu —
 * `flattened.slice(0, 5)` in the raw layout script.
 *
 * This is a SHORTCUT BAR, not the navigation. It used to be the only mobile
 * chrome (the top menu and its toggler were both hidden below lg), which left
 * the six destinations outside the slice — Packs, Mandates, Network, Enrich,
 * Refresh, Deals — with no route to them at all once you left the landing
 * page. The toggler now opens the full grouped menu on mobile, so this bar is
 * free to be what it was meant to be: quick access to the busiest few.
 */
export const MOBILE_BOTTOM_NAV_LIMIT = 5;

/**
 * The menu grouped into the pipeline the product is actually organised around:
 * source a parcel, verify it, match it to a mandate, work the deal. It is the
 * same claim the sign-in screen makes ("Source, verify and match land — end to
 * end") and the same order the design's role objectives run in.
 *
 * WHY GROUP AT ALL: the raw design declares `menuData` as one flat list, and
 * eleven equal-weight items is not an information architecture — nothing tells
 * you that Registry follows Listings, and at ~1180px the row collides with the
 * user menu. The design's own `moduleId`s (22380-22387) show grouping was
 * always intended; six of those eight modules hold a single page, so grouping
 * by module alone would still leave a near-flat list. These four stages are
 * that intent expressed at a useful altitude. Every item keeps its design ids.
 *
 * ORDER within a group follows the flow, not the alphabet: you capture before
 * you curate, adjudicate before you re-verify, match before you pack.
 */
export const MENU_GROUPS = [
  {
    label: 'Source',
    icon: 'fa fa-map-marked-alt',
    items: ['/capture', '/chats', '/listings'],
  },
  {
    label: 'Verify',
    icon: 'fa fa-stamp',
    items: ['/registry', '/enrich', '/refresh'],
  },
  {
    label: 'Match',
    icon: 'fa fa-sliders-h',
    items: ['/matching', '/packs'],
  },
  {
    label: 'Deals',
    icon: 'fa fa-handshake',
    items: ['/mandates', '/network', '/reach', '/deals'],
  },
];

const BY_PATH = new Map(MENU_ITEMS.map((item) => [item.path, item]));

/**
 * MENU_GROUPS resolved to real menu items.
 *
 * Throws rather than skipping a path that does not resolve: a typo here would
 * otherwise silently drop a page out of the navigation entirely, which is the
 * exact failure this grouping exists to fix.
 */
export const RESOLVED_MENU_GROUPS = MENU_GROUPS.map((group) => ({
  ...group,
  items: group.items.map((path) => {
    const item = BY_PATH.get(path);
    if (!item) {
      throw new Error(`menu-data: MENU_GROUPS references unknown path "${path}"`);
    }
    return item;
  }),
}));

/**
 * Guards the invariant the mobile bottom bar used to break: every destination
 * in MENU_ITEMS must appear in exactly one group, or it becomes unreachable.
 */
const grouped = RESOLVED_MENU_GROUPS.flatMap((g) => g.items.map((i) => i.path));
const ungrouped = MENU_ITEMS.filter((i) => !grouped.includes(i.path));
if (ungrouped.length > 0) {
  throw new Error(
    `menu-data: these menu items are in no group and would be unreachable: ${ungrouped
      .map((i) => i.path)
      .join(', ')}`
  );
}
